import type { EducationSession } from '@platform/billing';
import { SEOUL_TZ } from '@platform/datetime';

const KRW = new Intl.NumberFormat('ko-KR');

/** `29,000원` / 0원은 '무료'. 스토어 formatKRW 와 같은 표기를 쓴다. */
export function formatEducationPrice(price: number): string {
  return price === 0 ? '무료' : `${KRW.format(price)}원`;
}

/**
 * 교육 일정은 **한국시간으로 고정**해 보여준다. 브라우저 타임존을 따르면 해외에서
 * 접속한 사람에게 '10월 15일 09:00' 교육이 다른 날짜·시각으로 보이고, 그 값으로
 * 비행기표를 끊는다. 교육이 열리는 곳의 시각이 유일한 진실이다.
 *
 * 연도도 뺄 수 없다 — 몇 달 뒤 회차가 같은 목록에 걸리므로 '10월 15일' 만으로는
 * 올해인지 내년인지 구분이 안 된다.
 */
const DATE_TIME = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_ONLY = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

const SESSION_DAY = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  month: 'short',
  day: 'numeric',
});

const SESSION_WEEKDAY = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  weekday: 'short',
});

const SESSION_TIME = new Intl.DateTimeFormat('ko-KR', {
  timeZone: SEOUL_TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function formatSessionDateTime(iso: string): string {
  return DATE_TIME.format(new Date(iso));
}

export function formatDate(iso: string): string {
  return DATE_ONLY.format(new Date(iso));
}

/** 회차 목록의 날짜 열에서 날짜·요일·시각을 각각 정렬해 보여줄 때 쓴다. */
export function formatSessionDateParts(iso: string) {
  const date = new Date(iso);
  return {
    day: SESSION_DAY.format(date),
    weekday: SESSION_WEEKDAY.format(date),
    time: SESSION_TIME.format(date),
  };
}

/** 접수 버튼 아래에 띄울 한 줄. 왜 못 담는지를 사용자 말로 설명한다. */
export function sessionBlockReason(session: EducationSession): string | null {
  if (session.applicable) return null;
  if (session.status === 'CANCELED') return '개설이 취소된 회차입니다.';
  if (session.status === 'COMPLETED') return '종료된 회차입니다.';
  if (session.soldOut) return '정원이 마감되었습니다.';
  if (new Date(session.applyStartAt) > new Date())
    return `${formatDate(session.applyStartAt)}부터 접수합니다.`;
  return '접수 기간이 종료되었습니다.';
}
